/**
 * Pure GRID navigation math — the row/col cursor logic shared by every keyboard-driven
 * card/tile grid (RegionMap destinations, the Dex tiles, Team/Box cards). Free of Phaser
 * so it is unit-testable in a plain node environment (mirrors menuNav / loreNav /
 * pickerNav — the repo's pure-helper convention).
 *
 * The grid is filled ROW-MAJOR: index i lives at row = floor(i/cols), col = i%cols. Unlike
 * the wrapping vertical Menu, a 2-D grid does NOT wrap at its edges (pressing Left on the
 * first column stays put) — that's the read a player expects when arrowing across cards.
 * The one nicety: pressing Down from a column that has no tile in the (partial) last row
 * lands on the LAST item, so the final card is always reachable.
 */

export type GridDir = 'up' | 'down' | 'left' | 'right';

/** The row a row-major index sits in, for a grid `cols` wide. */
export function rowOf(index: number, cols: number): number {
  return Math.floor(index / Math.max(1, cols));
}

/** The column a row-major index sits in, for a grid `cols` wide. */
export function colOf(index: number, cols: number): number {
  return index % Math.max(1, cols);
}

/**
 * Move the cursor from `current` by `dir` across a row-major grid of `count` items laid
 * out `cols` columns wide. Clamps at every edge (no wrap); returns the current index when
 * a move would leave the grid. Down from a column past the (partial) last row lands on the
 * last item so nothing is unreachable. `current` is clamped into range first, so a stale
 * index is always safe.
 */
export function moveGrid(current: number, dir: GridDir, count: number, cols: number): number {
  if (count <= 0) return 0;
  const c = Math.max(1, cols);
  const cur = Math.max(0, Math.min(count - 1, current));
  const col = cur % c;

  switch (dir) {
    case 'left':
      return col > 0 ? cur - 1 : cur;
    case 'right':
      return col < c - 1 && cur + 1 < count ? cur + 1 : cur;
    case 'up':
      return cur - c >= 0 ? cur - c : cur;
    case 'down': {
      const target = cur + c;
      if (target < count) return target;
      // No tile directly below (we're above a partial last row, or on the last row).
      // If we're NOT already on the last row, drop to the last item so it's reachable.
      const lastRowStart = Math.floor((count - 1) / c) * c;
      return cur < lastRowStart ? count - 1 : cur;
    }
    default:
      return cur;
  }
}
